// Session Replay's `mask` privacy level does not mask every URL attribute.
// Hide only affected elements; never change links, form targets or media URLs.
const URL_ATTRIBUTES = [
  'action',
  'formaction',
  'href',
  'src',
  'srcset',
  'poster',
  'data',
  'cite',
  'background',
];
const MEDIA_SELECTOR = 'iframe,audio,video,object,embed,source';
let installed = false;

const unsafeUrl = (value) => {
  if (!value) return false;
  if (/[?#]/.test(value)) return true;
  try {
    const url = new URL(value, window.location.href);
    return Boolean(url.username || url.password);
  } catch {
    return true;
  }
};
// Preserve ordinary same-origin versioned stylesheets without permitting arbitrary query data.
const isSafeStylesheet = (element, name) => {
  if (
    element.tagName !== 'LINK' ||
    name !== 'href' ||
    !element.relList?.contains('stylesheet')
  )
    return false;
  try {
    const url = new URL(element.getAttribute('href'), window.location.href);
    const params = [...url.searchParams.entries()];
    return (
      url.protocol === 'https:' &&
      url.origin === window.location.origin &&
      !url.username &&
      !url.password &&
      !url.hash &&
      /\.css$/i.test(url.pathname) &&
      params.length === 1 &&
      /^(?:v|ver)$/.test(params[0][0]) &&
      /^(?=.{1,24}$)\d+(?:\.\d+){0,3}$/.test(params[0][1])
    );
  } catch {
    return false;
  }
};
// Static application hash routes carry no per-user identifiers. Every other
// query, fragment or URL credential is masked even inside readable subtrees.
const STATIC_HASH_ROUTES = new Set([
  '#/',
  '#/tasks',
  '#/classes',
  '#/docs',
  '#/giveFeedback',
  '#/feedbackHistory',
  '#/tools/commentbanks',
  '#/tools/rubrics',
  '#/tools/successcriterias',
  '#/tools/shortanswers',
  '#/jeddai',
  '#/m/home',
  '#/m/account',
  '#/m/library',
  '#/m/notifications',
]);
// Restrict opt-in to specific bundled assets, even if user HTML supplies the marker.
const STATIC_ICON_NAMES = new Set(
  'JeddAI-Icon-active Task2 active-class-insights-icon activeGetFeedback activesetting activetask exithubgray26 getfeedback32gray insights jeddaigray32 setting exams exams-active commentSelected commentUnSelected markSelected markUnSelected 24grayshare purple-share-icon rubric-outcome black-rubric-icon ShortAnsIcon PurpleShortAns advanced-controls advanced-controls-active current-tasks current-tasks-selected closed-tasks closed-tasks-selected exemplars exemplars-selected shared-with-me shared-with-me-selected view-requests view-requests-selected feedback-history feedback-history-selected'.split(
    ' '
  )
);
const isTrustedStaticIcon = (element, name) => {
  if (
    element.tagName !== 'IMG' ||
    name !== 'src' ||
    element.getAttribute('data-jeddle-replay-static-icon') !== 'true'
  )
    return false;
  try {
    const url = new URL(element.getAttribute('src'), window.location.href);
    const asset = url.pathname.match(
      /\/([^/]+)\.[a-f0-9]{8,}\.(?:svg|png|webp)$/i
    );
    return (
      url.origin === window.location.origin &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      (Boolean(asset && STATIC_ICON_NAMES.has(asset[1])) ||
        url.pathname === '/img/logo.svg')
    );
  } catch {
    return false;
  }
};
const protect = (element) => {
  if (element.nodeType !== 1) return;
  let privacy = element.matches(MEDIA_SELECTOR) ? 'hidden' : undefined;
  for (const name of URL_ATTRIBUTES) {
    if (
      !element.hasAttribute(name) ||
      isSafeStylesheet(element, name) ||
      isTrustedStaticIcon(element, name)
    )
      continue;
    const value = element.getAttribute(name);
    if ((name === 'action' || name === 'formaction') && value) {
      privacy = 'hidden';
      break;
    }
    if (
      element.tagName === 'A' &&
      name === 'href' &&
      STATIC_HASH_ROUTES.has(value)
    )
      continue;
    const maskable =
      element.namespaceURI === 'http://www.w3.org/1999/xhtml' &&
      ((element.tagName === 'A' && name === 'href') ||
        (element.tagName === 'IMG' && ['src', 'srcset'].includes(name)));
    if (maskable) {
      if (!privacy) privacy = 'mask';
      continue;
    }
    if (unsafeUrl(value)) {
      privacy = 'hidden';
      break;
    }
  }
  if (
    privacy &&
    element.getAttribute('data-dd-privacy') !== 'hidden' &&
    element.getAttribute('data-dd-privacy') !== privacy
  ) {
    element.setAttribute('data-dd-privacy', privacy);
  }
};
const scan = (node) => {
  if (node.nodeType !== 1) return;
  protect(node);
  node.querySelectorAll('*').forEach(protect);
};
export const installReplayUrlGuard = (onFailure = () => {}) => {
  if (installed) return true;
  let observer;
  try {
    if (!document.documentElement || typeof MutationObserver !== 'function')
      return false;
    observer = new MutationObserver((records) => {
      try {
        records.forEach((record) => {
          if (record.type === 'attributes') protect(record.target);
          else record.addedNodes.forEach(scan);
        });
      } catch {
        try {
          onFailure();
        } catch {
          /* Do not break the app. */
        }
      }
    });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        ...URL_ATTRIBUTES,
        'data-dd-privacy',
        'data-jeddle-replay-static-icon',
      ],
    });
    scan(document.documentElement);
    installed = true;
    return true;
  } catch {
    observer?.disconnect();
    return false;
  }
};
